"use client";

import { useEffect, useState } from "react";
import { MonitorIcon, MoonIcon, SunIcon } from "@phosphor-icons/react";
import { THEME_MEDIA_QUERY } from "@/lib/env";
import type { ThemeMode } from "@/lib/types";
import { cn } from "@/lib/utils";

// const THEME_SCRIPT = `
//   const doc = document.documentElement;
//   // const theme = localStorage.getItem("${THEME_STORAGE_KEY}") ?? "system";
//   const theme = "system"; // Hardcoded for demo

//   if (theme === "system") {
//     if (window.matchMedia("${THEME_MEDIA_QUERY}").matches) {
//       doc.classList.add("dark");
//     } else {
//       doc.classList.add("light");
//     }
//   } else {
//     doc.classList.add(theme);
//   }
// `
//   .trim()
//   .replace(/\n/g, '')
//   .replace(/\s+/g, ' ');

function applyTheme(theme: ThemeMode) {
  const doc = document.documentElement;

  doc.classList.remove("dark", "light");

  // Safe localStorage access - commented out for demo
  // if (typeof window !== 'undefined') {
  //   localStorage.setItem(THEME_STORAGE_KEY, theme); // Commented out for demo
  // }

  if (theme === "system") {
    if (window.matchMedia(THEME_MEDIA_QUERY).matches) {
      doc.classList.add("dark");
    } else {
      doc.classList.add("light");
    }
  } else {
    doc.classList.add(theme);
  }
}

interface ThemeToggleProps {
  className?: string;
}

export function ApplyThemeScript() {
  return <script id="theme-script" />; //dangerouslySetInnerHTML={{ __html: THEME_SCRIPT }}//
}

export function ThemeToggle({ className }: ThemeToggleProps) {
  const [theme, setTheme] = useState<ThemeMode | undefined>(undefined);
  const [isMounted, setIsMounted] = useState(false);

  // Set mounted state to ensure we're on client
  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!isMounted) return;

    // Safe localStorage access - commented out for demo
    const storedTheme: ThemeMode = "system"; // Default value for demo
    // if (typeof window !== "undefined") {
    //   storedTheme = (localStorage.getItem(THEME_STORAGE_KEY) as ThemeMode) ?? 'system';
    // }
    setTheme(storedTheme);
  }, [isMounted]);

  function handleThemeChange(theme: ThemeMode) {
    if (!isMounted) return;
    applyTheme(theme);
    setTheme(theme);
  }

  // Don't render until mounted to avoid hydration mismatch
  if (!isMounted) {
    return (
      <div
        className={cn(
          "text-foreground bg-background flex w-full flex-row justify-end divide-x overflow-hidden rounded-full border",
          className,
        )}
      >
        {/* Placeholder with same dimensions */}
        <button type="button" className="cursor-pointer p-1 pl-1.5 opacity-25">
          <MoonIcon size={16} weight="bold" />
        </button>
        <button type="button" className="cursor-pointer px-1.5 py-1 opacity-25">
          <SunIcon size={16} weight="bold" />
        </button>
        <button type="button" className="cursor-pointer p-1 pr-1.5 opacity-25">
          <MonitorIcon size={16} weight="bold" />
        </button>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "text-foreground bg-background flex w-full flex-row justify-end divide-x overflow-hidden rounded-full border",
        className,
      )}
    >
      <span className="sr-only">Color scheme toggle</span>
      <button
        type="button"
        onClick={() => handleThemeChange("dark")}
        className="cursor-pointer p-1 pl-1.5"
      >
        <span className="sr-only">Enable dark color scheme</span>
        <MoonIcon
          size={16}
          weight="bold"
          className={cn(theme !== "dark" && "opacity-25")}
        />
      </button>
      <button
        type="button"
        onClick={() => handleThemeChange("light")}
        className="cursor-pointer px-1.5 py-1"
      >
        <span className="sr-only">Enable light color scheme</span>
        <SunIcon
          size={16}
          weight="bold"
          className={cn(theme !== "light" && "opacity-25")}
        />
      </button>
      <button
        type="button"
        onClick={() => handleThemeChange("system")}
        className="cursor-pointer p-1 pr-1.5"
      >
        <span className="sr-only">Enable system color scheme</span>
        <MonitorIcon
          size={16}
          weight="bold"
          className={cn(theme !== "system" && "opacity-25")}
        />
      </button>
    </div>
  );
}
